import { Patient, Alert, db, savePatients, saveAlerts } from './db';

// Sample patient data - expanded from seeds
export const samplePatients: Patient[] = [
  { 
    id: '1', 
    name: 'Jane Doe', 
    priority: 'High', 
    points: 1200,
    lastUpdated: Date.now(),
    syncStatus: 'synced'
  },
  { 
    id: '2', 
    name: 'John Smith', 
    priority: 'Medium', 
    points: 800,
    lastUpdated: Date.now(),
    syncStatus: 'synced'
  },
  { 
    id: '3', 
    name: 'Maria Garcia', 
    priority: 'High', 
    points: 1500,
    lastUpdated: Date.now(),
    syncStatus: 'synced'
  },
  { 
    id: '4', 
    name: 'Robert Johnson', 
    priority: 'Low', 
    points: 450,
    lastUpdated: Date.now(),
    syncStatus: 'synced'
  },
  { 
    id: '5', 
    name: 'Emily Chen', 
    priority: 'Medium', 
    points: 950,
    lastUpdated: Date.now(),
    syncStatus: 'synced'
  },
  { 
    id: '6', 
    name: 'Michael Williams', 
    priority: 'High', 
    points: 1800,
    lastUpdated: Date.now(),
    syncStatus: 'synced'
  },
  { 
    id: '7', 
    name: 'Sarah Davis', 
    priority: 'Low', 
    points: 320,
    lastUpdated: Date.now(),
    syncStatus: 'synced'
  },
  { 
    id: '8', 
    name: 'David Martinez', 
    priority: 'Medium', 
    points: 670,
    lastUpdated: Date.now(),
    syncStatus: 'synced'
  }
];

// Sample alert data - expanded from seeds
export const sampleAlerts: Alert[] = [
  { 
    id: 'a1', 
    title: 'Elevated Blood Pressure - Immediate Attention', 
    patient: 'Jane Doe',
    severity: 'critical',
    timestamp: Date.now() - 300000, // 5 minutes ago
    acknowledged: false,
    lastUpdated: Date.now(),
    syncStatus: 'synced'
  },
  { 
    id: 'a2', 
    title: 'Low Oxygen Saturation Detected', 
    patient: 'John Smith',
    severity: 'high',
    timestamp: Date.now() - 900000, // 15 minutes ago
    acknowledged: false,
    lastUpdated: Date.now(),
    syncStatus: 'synced'
  },
  { 
    id: 'a3', 
    title: 'Medication Due - Pain Management', 
    patient: 'Maria Garcia',
    severity: 'medium',
    timestamp: Date.now() - 1800000, // 30 minutes ago
    acknowledged: false,
    lastUpdated: Date.now(),
    syncStatus: 'synced'
  },
  { 
    id: 'a4', 
    title: 'Irregular Heart Rate Pattern', 
    patient: 'Michael Williams',
    severity: 'critical',
    timestamp: Date.now() - 600000, // 10 minutes ago
    acknowledged: false,
    lastUpdated: Date.now(),
    syncStatus: 'synced'
  },
  { 
    id: 'a5', 
    title: 'Temperature Spike Detected', 
    patient: 'Emily Chen',
    severity: 'high',
    timestamp: Date.now() - 1200000, // 20 minutes ago
    acknowledged: false,
    lastUpdated: Date.now(),
    syncStatus: 'synced'
  },
  { 
    id: 'a6', 
    title: 'Scheduled Vitals Check', 
    patient: 'Robert Johnson',
    severity: 'low',
    timestamp: Date.now() - 2700000, // 45 minutes ago
    acknowledged: true,
    lastUpdated: Date.now(),
    syncStatus: 'synced'
  },
  { 
    id: 'a7', 
    title: 'Lab Results Available for Review', 
    patient: 'Sarah Davis',
    severity: 'medium',
    timestamp: Date.now() - 3600000, // 1 hour ago
    acknowledged: true,
    lastUpdated: Date.now(),
    syncStatus: 'synced'
  },
  { 
    id: 'a8', 
    title: 'Patient Call Button Activated', 
    patient: 'David Martinez',
    severity: 'medium',
    timestamp: Date.now() - 180000, // 3 minutes ago
    acknowledged: false,
    lastUpdated: Date.now(),
    syncStatus: 'synced'
  }
];

/**
 * Load sample data into IndexedDB
 * Called on first login or when database is empty
 */
export async function loadSampleData(): Promise<void> {
  try {
    // Check if data already exists
    const existingPatients = await db.patients.count();
    const existingAlerts = await db.alerts.count();

    // Only load if database is empty
    if (existingPatients === 0) {
      console.log('Loading sample patient data...');
      await savePatients(samplePatients);
    }

    if (existingAlerts === 0) {
      console.log('Loading sample alert data...');
      await saveAlerts(sampleAlerts);
    }

    console.log('Sample data loaded successfully');
  } catch (error) {
    console.error('Failed to load sample data:', error);
    throw error;
  }
}

/**
 * Refresh sample data (useful for demo/testing)
 */
export async function refreshSampleData(): Promise<void> {
  try {
    console.log('Refreshing sample data...');
    await savePatients(samplePatients);
    await saveAlerts(sampleAlerts);
    console.log('Sample data refreshed successfully');
  } catch (error) {
    console.error('Failed to refresh sample data:', error);
    throw error;
  }
}
